import { customElement, property } from "lit/decorators.js";
import { msg } from "@omegagrid/localize";
import { ToolbarDropdown } from "@omegagrid/toolbar";
import { style } from "./sqlEditor.style";
import { computeCompileTail, filterSqlMessage, SqlDataset, SqlEditorAdapter, SqlMessage, SqlQueryPlan } from "./sqlEditorAdapter";
import { CodeEditor } from "./codeEditor";
import { getEditorSettings } from "./editorSettings";
import { actions, dom, log } from "@omegagrid/core";
import { ToolbarButton } from "@omegagrid/toolbar";
import { debounce } from "ts-debounce";
import { SqlParseResult, ToolbarItemOptions } from "../types";

export class SqlEditorEvent extends Event {

	dataset: SqlDataset;
	message: SqlMessage;
	queryPlan: SqlQueryPlan;
	sql: string;
	/** compile only: number of lines the application prepends before the user's code */
	offset: number;
	/** compile only: number of lines the application appends after the user's code */
	tail: number;

	constructor(type: string, args?: Partial<SqlEditorEvent>) {
		super(`sqleditor.${type}`, {composed: true, bubbles: true});
		Object.assign(this, args);
	}

}

function formatDuration(ms: number) {
	const minutes = Math.floor(ms / 60000);
	const seconds = Math.floor((ms % 60000) / 1000);
	const milliseconds = Math.floor(ms % 1000);
	return [
	  ((minutes < 10 ? '0' : '') + minutes).toString(),
	  ((seconds < 10 ? '0' : '') + seconds).toString(),
	  ((milliseconds < 100 ? (milliseconds < 10 ? '00' : '0') : '') + milliseconds).toString()
	].join(':');
  }

const DEFAULT_MAX_ROWS = 1000;

@customElement('rp-editor-sql')
export class SqlEditor extends CodeEditor {

	static styles = [...CodeEditor.styles, style];


	@property({type: Object})
	adapter: SqlEditorAdapter;

	private timer: number;
	private _parsingEnabled = true;
	private _hasExecButton = false;

	async willUpdate(props: Map<PropertyKey, unknown>) {
		await super.willUpdate(props);
		this.editorOptions.colorDecorators = false;
		if (this.options) this.options.language = 'sql';
	}

	async updated(props: Map<PropertyKey, unknown>) {
		await super.updated(props);
		if (this.toolbar && !this.toolbar.values?.top) this.toolbar.values = {top: DEFAULT_MAX_ROWS, ...this.toolbar.values};
	}

	async createToolbarItems() {
		const options = await this.adapter.getOptions();
		let items: ToolbarItemOptions[] = [];

		for (const key in options.toolbar) {
			const itemDef = options.toolbar[key as keyof typeof options.toolbar];
			if (itemDef.enabled === false) continue;
			if (key === 'exec' && !itemDef.disabled) this._hasExecButton = true;
			const item = {id: key, ...itemDef} as ToolbarItemOptions;
			items.push(item);
		}

		items.sort((a, b) => (a.ord || 0) - (b.ord || 0));
		return items;
	}

	startTimer(button: ToolbarButton) {
		const durationElm = button.querySelector('#duration') as HTMLSpanElement;
		const start = window.performance.now();
		this.timer = window.setInterval(() => {
			durationElm.innerText = formatDuration(window.performance.now() - start);
		}, 10);
	}

	endTimer() {
		if (this.timer != null) {
			window.clearInterval(this.timer);
			this.timer = null;
		}
	}

	async exec(withQueryPlan = false) {
		const execButton = this.toolbar.controls.get('exec') as ToolbarButton;
		const stopButton = this.toolbar.controls.get('stop') as ToolbarButton;
		const topSelect = this.toolbar.controls.get('top') as ToolbarDropdown;
		const dbSelect = this.toolbar.controls.get('db') as ToolbarDropdown;

		dom.hideElement(execButton);
		dom.showElement(stopButton);
		this.startTimer(stopButton);

		this.dispatchEvent(new SqlEditorEvent('beforeExec'));

		let anyOutput = false;
		// the warnings / print output the user does not want to see stop here, so "no output" stays truthful
		const messagesLevel = getEditorSettings().messages;
		await this.adapter.exec(this.editor.selectedContent, {
			top: parseInt(topSelect.value as string),
			queryPlan: withQueryPlan,
			database: dbSelect?.value,
		}, ({dataset, message, queryPlan}) => {
			message = filterSqlMessage(message, messagesLevel);
			if (dataset || message || queryPlan) anyOutput = true;
			this.dispatchEvent(new SqlEditorEvent('result', {dataset, message, queryPlan}));
		});

		if (!anyOutput) {
			this.dispatchEvent(new SqlEditorEvent('result', {message: {index: 0, content: msg('Completed with no output')}}));
		}

		this.endTimer();
		dom.showElement(execButton, 'flex');
		dom.hideElement(stopButton);
	}

	async compile() {
		const execButton = this.toolbar.controls.get('exec') as ToolbarButton;
		const stopButton = this.toolbar.controls.get('stop') as ToolbarButton;

		dom.hideElement(execButton);
		dom.showElement(stopButton);
		this.startTimer(stopButton);

		try {
			const sql = this.editor.selectedContent;
			const result = await this.adapter.compile(sql);
			const compiled = typeof result === 'string' ? { sql: result } : result;
			const offset = compiled?.offset ?? 0;
			const tail = compiled?.tail ?? computeCompileTail(sql, compiled?.sql, offset);
			this.dispatchEvent(new SqlEditorEvent('compile', { sql: compiled?.sql, offset, tail }));
		} catch (error) {
			log.getLogger('rp').error('SQL Compile Error:', error);
		}

		this.endTimer();
		dom.showElement(execButton, 'flex');
		dom.hideElement(stopButton);
	}

	debounceParse = debounce(this.parse, 500); //, {isImmediate: false, maxWait: 50});

	async parse() {
		if (!this.editor) return;
		if (!this.editor.content) return;
		if (this.options?.readOnly) return;

		const sql = this.editor.content;
		let res: SqlParseResult | void;
		try {
			res = await this.adapter.parse(sql);
		} catch (error) {
			log.getLogger('rp').warn(`SQL Parse Error: ${error}`);
			return;
		}
		if (!res) {
			this._parsingEnabled = false;
			return;
		}

		// the content changed while the server was parsing (undo, a deleted selection): the errors
		// belong to text that is no longer there and the edit that changed it has already scheduled
		// a fresh parse; the editor may also have been closed meanwhile
		const model = this.editor?.editor?.getModel();
		if (!model || this.editor.content !== sql) return;
		const lineCount = model.getLineCount();
		const markers: {
			message: string,
			startLineNumber: number,
			startColumn: number,
			endLineNumber: number,
			endColumn: number,
			severity: number
		}[] = [];

		res.errors.forEach(error => {
			// an error without a line (the compile phase itself failed) cannot point anywhere
			// meaningful - flag the whole first line as a warning instead of a hard error
			const lineKnown = error.line > 0;
			// a line past the end (the compiled SQL is longer than the source) is shown on the last line
			const lineNumber = lineKnown ? Math.min(error.line, lineCount) : 1;
			const line = model.getLineContent(lineNumber);
			const startColumn = lineKnown && error.keyword ? line.indexOf(error.keyword) + 1 : 0;
			markers.push({
				message: lineKnown ? error.error : `${msg('Unknown line')}: ${error.error}`,
				severity: lineKnown
					? this.editor.monaco.MarkerSeverity.Error
					: this.editor.monaco.MarkerSeverity.Warning,
				startLineNumber: lineNumber,
				startColumn: Math.max(1, startColumn),
				endLineNumber: lineNumber,
				endColumn: startColumn > 0 ? startColumn + error.keyword.length : line.length,
			});
		});
		this.editor.monaco.editor.setModelMarkers(model, "owner", markers);
	}

	abort() {
		this.adapter.abort();
	}

	edit() {
		super.edit();
		if (this._parsingEnabled) this.debounceParse();
	}

	async ready() {
		await super.ready();

		// additional keyboard shortcut using monaco editor onKeyDown
		this.editor.editor.onKeyDown((e) => {
			if (this._hasExecButton && e.ctrlKey && e.code === 'KeyB') {
				e.preventDefault();
				e.stopPropagation();
				this.exec();
			}
		});

		this.debounceParse();
	}


	async _onToolbarAction(e: actions.ActionEvent) {
		if (e.action.key === 'stop') {
			const command = await this.host.openMenu({
				content: 'yes_no',
				text: msg('Are you sure you want to stop the query?'),
			});
			if (command?.id == 'Y') {
				this.abort();
			}
			return;
		}

		if (e.action.type == 'select') {
			switch (e.action.key) {
				case 'exec': this.exec(); break;
				case 'explain': this.exec(true); break;
				case 'compile': this.compile(); break;
			}
		} else {
			switch (e.action.key) {
				case 'save': this.save(); break;
				case 'discard': this.discard(); break;
			}
		}
	}

}
